import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, CheckCircle, ChevronRight, AlertCircle, ArrowLeft } from 'lucide-react';
import {
  useFeedbackById,
  useUpdateFeedback,
  useProcessMessage,
  useAddConversationMessage,
  useEpicById,
  useAssistantVoice,
  useHistoricalContext,
} from '@/hooks/usePerformance';
import { ScorePill } from '@/components/performance/ScorePill';
import { EpicTypeBadge } from '@/components/performance/EpicTypeBadge';
import { ConversationalMode } from '@/components/performance/ConversationalMode';
import { TranscriptEntry } from '@/hooks/useLiveAudioSession';
import { toast } from 'sonner';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MarkdownText } from '@/components/performance/MarkdownText';

interface ChatMessage {
  role: 'user' | 'model';
  text: string;
}

interface DimensionState {
  id: string;
  score: number | null;
  justification: string;
  hasEnoughInfo: boolean;
}

interface EvaluationState {
  clapperName?: string;
  impactLevel?: string;
  summary?: string;
  dimensions: DimensionState[];
  generalComments: { effective: string; affective: string; perspective: string };
}


export function PerformanceEvaluation() {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const mode = (location.state as any)?.mode ?? 'chat';

  const { data: feedback, isLoading: loadingFeedback } = useFeedbackById(feedbackId!);
  const { data: epic } = useEpicById(feedback?.epicGlobalId ?? '');
  const { data: assistantVoice } = useAssistantVoice(epic ? (epic.assistantId ?? 'default') : undefined);
  const { data: historicalCtx } = useHistoricalContext(
    feedback?.clapperEmail ?? '',
    (feedback?.epicType as any) ?? 'seasonal',
    feedbackId!,
  );
  const processMessage = useProcessMessage();
  const addConvMessage = useAddConversationMessage();
  const updateFeedback = useUpdateFeedback();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [evalState, setEvalState] = useState<EvaluationState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedback && epic && !hasStarted) {
      const initialState: EvaluationState = {
        clapperName: `${feedback.clapperName} ${feedback.clapperLastName}`,
        impactLevel: feedback.impactLevel,
        dimensions: (feedback.dimensions ?? []).map((d: any) => ({
          id: d.id,
          score: d.score ?? null,
          justification: d.justification ?? '',
          hasEnoughInfo: d.hasEnoughInfo ?? false,
        })),
        generalComments: feedback.generalComments ?? { effective: '', affective: '', perspective: '' },
      };
      setEvalState(initialState);
      sendInitialGreeting(initialState);
      setHasStarted(true);
    }
  }, [feedback, epic, hasStarted]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  function getLangDirective(): string {
    const lang = i18n.language;
    if (lang.startsWith('en')) return 'Respond in English.';
    if (lang.startsWith('pt')) return 'Responda em Português.';
    return 'Responde en Español.';
  }

  async function sendInitialGreeting(state: EvaluationState) {
    if (!feedback || !epic) return;
    try {
      const response = await processMessage.mutateAsync({
        assistantId: epic.assistantId ?? 'default',
        epicId: feedback.epicGlobalId,
        epicType: feedback.epicType as any ?? 'seasonal',
        messages: [],
        currentState: {
          clapperName: state.clapperName,
          impactLevel: state.impactLevel,
          dimensions: state.dimensions,
          generalComments: state.generalComments,
        },
        dimensions: (epic.dimensions ?? []).map((d: any) => ({
          id: d.id,
          category: d.category,
          name: d.name,
          description: d.description ?? '',
          descriptions: d.descriptions,
          questions: d.questions,
        })),
        reports: [],
        languageDirective: getLangDirective() + (mode === 'voice' ? ' MODO VOZ: Presentate brevemente y hacé la primera pregunta de evaluación de forma concisa y conversacional.' : ''),
        historicalContext: historicalCtx?.context,
      });

      const assistantText = response.replyToUser;
      setMessages([{ role: 'model', text: assistantText }]);
      updateEvalState(response, state);
    } catch (err) {
      setMessages([{ role: 'model', text: '¡Hola! Estoy lista para ayudarte con la evaluación. ¿Empezamos?' }]);
    }
  }

  function updateEvalState(response: any, prev: EvaluationState): EvaluationState {
    const newState: EvaluationState = {
      clapperName: response.evaluationState?.clapperName ?? prev.clapperName,
      impactLevel: response.evaluationState?.impactLevel ?? prev.impactLevel,
      dimensions: response.evaluationState?.dimensions ?? prev.dimensions,
      generalComments: response.evaluationState?.generalComments ?? prev.generalComments,
      summary: response.summary ?? prev.summary,
    };
    setEvalState(newState);
    return newState;
  }

  async function handleSend() {
    if (!inputText.trim() || !feedback || !epic || !evalState) return;
    const userText = inputText.trim();
    setInputText('');

    const newMessages: ChatMessage[] = [...messages, { role: 'user', text: userText }];
    setMessages(newMessages);

    try {
      const response = await processMessage.mutateAsync({
        assistantId: epic.assistantId ?? 'default',
        epicId: feedback.epicGlobalId,
        epicType: feedback.epicType as any ?? 'seasonal',
        messages: newMessages.map((m) => ({ role: m.role, text: m.text })),
        currentState: {
          clapperName: evalState.clapperName,
          impactLevel: evalState.impactLevel,
          dimensions: evalState.dimensions,
          generalComments: evalState.generalComments,
        },
        dimensions: (epic.dimensions ?? []).map((d: any) => ({
          id: d.id,
          category: d.category,
          name: d.name,
          description: d.description ?? '',
          descriptions: d.descriptions,
          questions: d.questions,
        })),
        reports: [],
        languageDirective: getLangDirective(),
        historicalContext: historicalCtx?.context,
      });

      const assistantText = response.replyToUser;
      const withReply: ChatMessage[] = [...newMessages, { role: 'model', text: assistantText }];
      setMessages(withReply);
      const newState = updateEvalState(response, evalState);

      // Persist conversation message
      if (feedback.conversationId) {
        await addConvMessage.mutateAsync({
          conversationId: feedback.conversationId,
          epicFeedbackId: feedbackId!,
          role: 'user',
          text: userText,
          inputType: 'text',
        }).catch(() => {});
        await addConvMessage.mutateAsync({
          conversationId: feedback.conversationId,
          epicFeedbackId: feedbackId!,
          role: 'assistant',
          text: assistantText,
          inputType: 'text',
        }).catch(() => {});
      }

      // Auto-save dimensions/comments
      await updateFeedback.mutateAsync({
        id: feedbackId!,
        dimensions: newState.dimensions,
        generalComments: newState.generalComments,
      }).catch(() => {});

    } catch {
      toast.error(t('performance.evaluation.process_error'));
    }
  }

  const handleFinish = useCallback(async () => {
    if (!evalState) return;
    setIsSubmitting(true);
    try {
      await updateFeedback.mutateAsync({
        id: feedbackId!,
        dimensions: evalState.dimensions,
        generalComments: evalState.generalComments,
        status: 'completed',
      });

      // replace:true so browser-back from the result page skips the finished chat
      const epicType = feedback?.epicType ?? 'seasonal';
      if (epicType === 'feedback_session') {
        navigate(`/performance/epic/${feedbackId}/feedback-result`, { replace: true });
      } else if (epicType === 'performance') {
        navigate(`/performance/epic/${feedbackId}/performance-review`, { replace: true });
      } else {
        navigate(`/performance/epic/${feedbackId}/analysis`, { replace: true });
      }
    } catch {
      toast.error(t('performance.evaluation.finish_error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [evalState, feedbackId, feedback, navigate, updateFeedback]);

  // El texto llega ya transcripto por el navegador (Web Speech API) — sin audio.
  const handleVoiceMessage = useCallback(async (voiceText: string) => {
    if (!feedback || !epic || !evalState) return '';

    const response = await processMessage.mutateAsync({
      assistantId: epic.assistantId ?? 'default',
      epicId: feedback.epicGlobalId,
      epicType: (feedback.epicType as any) ?? 'seasonal',
      messages: [
        ...messages.map((m) => ({ role: m.role, text: m.text })),
        { role: 'user' as const, text: voiceText },
      ],
      currentState: {
        clapperName: evalState.clapperName,
        impactLevel: evalState.impactLevel,
        dimensions: evalState.dimensions,
        generalComments: evalState.generalComments,
      },
      dimensions: (epic.dimensions ?? []).map((d: any) => ({
        id: d.id, category: d.category, name: d.name,
        description: d.description ?? '', descriptions: d.descriptions, questions: d.questions,
      })),
      reports: [],
      languageDirective: getLangDirective() + ' MODO VOZ: Guiá la conversación evaluando UNA competencia por vez. Hacé una pregunta concreta y esperá la respuesta antes de pasar a la siguiente. Sé breve y conversacional.',
      historicalContext: historicalCtx?.context,
    });

    const replyText = response.replyToUser;

    // Update messages and eval state
    const userText = voiceText || '[Audio]';
    const withMessages: ChatMessage[] = [...messages, { role: 'user', text: userText }, { role: 'model', text: replyText }];
    setMessages(withMessages);
    const newState = updateEvalState(response, evalState);

    // Persist
    if (feedback.conversationId) {
      addConvMessage.mutateAsync({ conversationId: feedback.conversationId, epicFeedbackId: feedbackId!, role: 'user', text: userText, inputType: 'voice' }).catch(() => {});
      addConvMessage.mutateAsync({ conversationId: feedback.conversationId, epicFeedbackId: feedbackId!, role: 'assistant', text: replyText, inputType: 'text' }).catch(() => {});
    }
    await updateFeedback.mutateAsync({ id: feedbackId!, dimensions: newState.dimensions, generalComments: newState.generalComments }).catch(() => {});

    return replyText;
  }, [feedback, epic, evalState, messages, processMessage, addConvMessage, updateFeedback, feedbackId, historicalCtx]);

  const handleVoiceSessionEnd = useCallback(
    async (_transcript: TranscriptEntry[]) => {
      await handleFinish();
    },
    [handleFinish]
  );

  if (loadingFeedback) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex items-center justify-center h-64 gap-2 text-muted-foreground">
        <AlertCircle className="h-5 w-5" />
        <span>{t('performance.evaluation.evaluacion_no_encontrada')}</span>
      </div>
    );
  }

  // Voice mode
  if (mode === 'voice' && feedback && epic) {
    return (
      <div className="flex flex-col md:flex-row h-[calc(100vh-4rem)] overflow-hidden">
        {/* Dimension sidebar — same as chat mode */}
        <aside className="w-full md:w-72 border-b md:border-b-0 md:border-r bg-card flex flex-col shrink-0 hidden md:flex">
          <div className="p-4 border-b">
            <button
              onClick={() => navigate('/performance')}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Performance
            </button>
            <EpicTypeBadge type={(feedback.epicType as any) ?? 'seasonal'} />
            <p className="font-semibold mt-2 text-sm">{feedback.clapperName} {feedback.clapperLastName}</p>
            <p className="text-xs text-muted-foreground">{feedback.impactLevel}</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-2">
              {evalState?.dimensions.map((dim) => (
                <div key={dim.id} className="rounded-lg border bg-background p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase">{dim.id}</span>
                    {feedback.epicType !== 'performance' ? (
                      <ScorePill score={dim.score} />
                    ) : (
                      dim.hasEnoughInfo && <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                  </div>
                  {dim.justification && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{dim.justification}</p>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="p-4 border-t">
            <Button className="w-full" onClick={handleFinish} disabled={isSubmitting}>
              {isSubmitting ? <WinclapLoader size={16} className="mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
              {t('performance.evaluation.submit')}
            </Button>
          </div>
        </aside>

        {/* Voice UI */}
        <div className="flex-1 overflow-y-auto">
          <ConversationalMode
            // La voz vive en el asistente (flaviaConfig.voice), no en el EPIC.
            voice={assistantVoice?.voice ?? 'Lupe'}
            initialMessage={messages.find((m) => m.role === 'model')?.text}
            onProcessMessage={handleVoiceMessage}
            onEndSession={handleVoiceSessionEnd}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-4rem)] overflow-hidden">
      {/* Sidebar: Competencies */}
      <aside className="w-full md:w-72 border-b md:border-b-0 md:border-r bg-card flex flex-col shrink-0 hidden md:flex">
        <div className="p-4 border-b">
          <EpicTypeBadge type={(feedback.epicType as any) ?? 'seasonal'} />
          <p className="font-semibold mt-2 text-sm">{feedback.clapperName} {feedback.clapperLastName}</p>
          <p className="text-xs text-muted-foreground">{feedback.impactLevel}</p>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {evalState?.dimensions.map((dim) => (
              <div key={dim.id} className="rounded-lg border bg-background p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase">{dim.id}</span>
                  {feedback.epicType !== 'performance' ? (
                    <ScorePill score={dim.score} />
                  ) : (
                    dim.hasEnoughInfo && (
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                    )
                  )}
                </div>
                {dim.justification && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{dim.justification}</p>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="p-4 border-t">
          <Button
            className="w-full"
            onClick={handleFinish}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <WinclapLoader size={16} className="mr-2" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2" />
            )}
            {t('performance.evaluation.submit')}
          </Button>
        </div>
      </aside>

      {/* Chat area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b px-4 py-3 flex items-center justify-between shrink-0">
          <div>
            <h1 className="font-semibold text-sm">{t('performance.evaluation.title')}</h1>
            <p className="text-xs text-muted-foreground">{feedback.clapperName} {feedback.clapperLastName}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleFinish} disabled={isSubmitting}>
            {isSubmitting ? <WinclapLoader size={16} /> : t('performance.evaluation.submit')}
          </Button>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef as any}>
          <div className="space-y-4 max-w-4xl mx-auto">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-[15px] ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  {msg.role !== 'user'
                    ? <MarkdownText>{msg.text}</MarkdownText>
                    : <p className="whitespace-pre-wrap">{msg.text}</p>
                  }
                </div>
              </div>
            ))}
            {processMessage.isPending && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-2.5">
                  <WinclapLoader size={16} className="text-foreground/30" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="border-t p-4 shrink-0">
          <div className="flex gap-2 max-w-4xl mx-auto">
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('performance.evaluation.message_placeholder')}
              className="min-h-[44px] max-h-32 resize-none"
              rows={1}
            />
            <Button
              onClick={handleSend}
              disabled={!inputText.trim() || processMessage.isPending}
              size="icon"
              className="h-11 w-11 shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
